import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Modal from '@/v3-dashboard/components/UI/Modal'
import DLoading from '@/components/DLoading'
import mixpanel from '@/utils/mixpanel'
import useToast from '@/hooks/useToast'
import { useUserStore } from '@/stores/user/userStore'
import {
  scheduleHibernation,
  getDowngradeInsights,
  saveDowngradeReason,
  saveDowgradeBookMeeting,
  getCheckoutSession,
  saveDowngradeStep,
  finishCancelDowngrade,
  submitFeedback,
  saveChangedTier,
  applyDiscountOffer,
} from '@/services/plans.service'
import StepReason from './StepReason'
import StepExpensive from './StepExpensive'
import StepNoValue from './StepNoValue'
import StepTiming from './StepTiming'
import StepBroken from './StepBroken'
import StepCompetitor from './StepCompetitor'
import StepDiscount from './StepDiscount'
import StepFeedback from './StepFeedback'

const REASON_BRANCH_MAP = {
  expensive: 'expensive',
  novalue: 'novalue',
  timing: 'timing',
  broken: 'broken',
  competitor: 'competitor',
}

/** Reasons whose first screen already includes the $5/mo pause offer (before discount) */
const PAUSE_OFFER_BEFORE_DISCOUNT_REASONS = new Set(['expensive', 'timing'])

/** Steps that read `downgradeInsights` — only these wait on getDowngradeInsights(); reason/broken/competitor/feedback render without it */
const STEPS_REQUIRING_INSIGHTS = new Set(['expensive', 'novalue', 'timing', 'discount'])

const STEP_TITLES = {
  reason: "What's the main reason you're leaving?",
  expensive: "Let's find you a better price",
  novalue: "Let's fix this together",
  timing: 'Pause your subscription',
  broken: 'We want to fix this for you',
  competitor: 'Before you switch...',
  discount: 'One last thing',
  feedback: 'Thank you for trusting us',
}

const DOWNGRADE_FLOW_CALENDLY_URL = 'https://calendly.com/dante-ai-support/dante-ai-call'

/** Plans page passes a tier slug string; modals may pass `{ name }`. */
function getNextTierSlug(nextTier) {
  if (nextTier == null) return undefined
  if (typeof nextTier === 'string') return nextTier.toLowerCase()
  const name = nextTier?.name
  return typeof name === 'string' ? name.toLowerCase() : undefined
}

const DowngradeFlowModal = ({ isOpen, onClose: propOnClose, nextTier, onDataLoaded }) => {
  const user = useUserStore((state) => state.user)
  const { addSuccessToast } = useToast()

  const [currentStep, setCurrentStep] = useState('reason')
  const [downgradeInsights, setDowngradeInsights] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isPauseLoading, setIsPauseLoading] = useState(false)
  const [isDowngradeLoading, setIsDowngradeLoading] = useState(false)
  const [isBookSessionLoading, setIsBookSessionLoading] = useState(false)
  const [isDiscountLoading, setIsDiscountLoading] = useState(false)
  const [isCancelLoading, setIsCancelLoading] = useState(false)
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false)
  const [sawPauseOfferBeforeDiscount, setSawPauseOfferBeforeDiscount] = useState(false)
  const [timingAfterDiscount, setTimingAfterDiscount] = useState(false)

  const closedByActionRef = useRef(false)

  const resetState = () => {
    setCurrentStep('reason')
    setDowngradeInsights(null)
    setIsLoading(false)
    setIsPauseLoading(false)
    setIsDowngradeLoading(false)
    setIsBookSessionLoading(false)
    setIsDiscountLoading(false)
    setIsCancelLoading(false)
    setIsFeedbackLoading(false)
    setSawPauseOfferBeforeDiscount(false)
    setTimingAfterDiscount(false)
    closedByActionRef.current = false
  }

  const handleClose = () => {
    if (!closedByActionRef.current) {
      mixpanel.track('Downgrade Flow V2 Abandoned', {
        step_reached: currentStep,
        current_tier: user?.tier_type,
        target_tier: getNextTierSlug(nextTier),
      })
    }

    if (downgradeInsights?.tier_downgrade_reason?.id) {
      saveDowngradeStep({
        tier_downgrade_reason_id: downgradeInsights.tier_downgrade_reason.id,
        step: currentStep,
      }).catch(() => {})
    }

    resetState()
    propOnClose()
  }

  const handleReasonSelect = (reason) => {
    if (downgradeInsights?.tier_downgrade_reason?.id) {
      saveDowngradeReason({
        tier_downgrade_reason_id: downgradeInsights.tier_downgrade_reason.id,
        reason,
      }).catch(() => {})
    }

    mixpanel.track('Downgrade Flow V2 Reason Selected', {
      reason,
      current_tier: user?.tier_type,
      target_tier: getNextTierSlug(nextTier),
    })

    setSawPauseOfferBeforeDiscount(PAUSE_OFFER_BEFORE_DISCOUNT_REASONS.has(reason))
    setTimingAfterDiscount(false)
    setCurrentStep(REASON_BRANCH_MAP[reason])
  }

  const handlePause = async () => {
    try {
      setIsPauseLoading(true)
      await scheduleHibernation()
      mixpanel.track('Hibernation Scheduled', {
        previous_tier: user?.tier_type,
        target_tier: 'hibernation',
        source: 'downgrade_flow_v2',
      })
      mixpanel.track('Downgrade Flow V2 Pause Selected', {
        current_tier: user?.tier_type,
        target_tier: getNextTierSlug(nextTier),
      })
      addSuccessToast({ message: 'Your subscription has been scheduled for hibernation.' })
      closedByActionRef.current = true
      handleClose()
      window.location.href = '/account-paused'
    } catch (error) {
      mixpanel.track('Hibernation Schedule Failed', {
        previous_tier: user?.tier_type,
        source: 'downgrade_flow_v2',
        error_message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'unknown',
      })
      addSuccessToast({
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          'Failed to pause subscription. Please try again.',
        type: 'error',
      })
    } finally {
      setIsPauseLoading(false)
    }
  }

  const handleDowngrade = async () => {
    try {
      setIsDowngradeLoading(true)
      const response = await getCheckoutSession(downgradeInsights?.tier?.id)
      if (response?.status === 200 && response?.data?.checkout_session?.url) {
        mixpanel.track('Downgrade Flow V2 Downgrade Selected', {
          current_tier: user?.tier_type,
          target_tier: downgradeInsights?.tier?.name?.toLowerCase(),
        })
        window.open(response.data.checkout_session.url, '_blank')
        closedByActionRef.current = true
        handleClose()
      }
    } catch (error) {
      addSuccessToast({
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          'Failed to process your request. Please try again.',
        type: 'error',
      })
    } finally {
      setIsDowngradeLoading(false)
    }
  }

  const handleBookSession = async () => {
    try {
      setIsBookSessionLoading(true)
      if (downgradeInsights?.tier_downgrade_reason?.id) {
        await saveDowgradeBookMeeting({
          tier_downgrade_reason_id: downgradeInsights.tier_downgrade_reason.id,
        })
      }
      mixpanel.track('Downgrade Flow V2 Optimization Session Booked', {
        current_tier: user?.tier_type,
        target_tier: getNextTierSlug(nextTier),
      })
      window.open(DOWNGRADE_FLOW_CALENDLY_URL, '_blank', 'noopener,noreferrer')
      closedByActionRef.current = true
      handleClose()
    } catch (error) {
      addSuccessToast({ message: 'Failed to book session. Please try again.', type: 'error' })
    } finally {
      setIsBookSessionLoading(false)
    }
  }

  const handleContactSupport = () => {
    mixpanel.track('Downgrade Flow V2 Support Clicked', {
      current_tier: user?.tier_type,
      target_tier: getNextTierSlug(nextTier),
    })
    window.open(DOWNGRADE_FLOW_CALENDLY_URL, '_blank', 'noopener,noreferrer')
  }

  const handleGoToDiscount = () => {
    mixpanel.track('Downgrade Flow V2 Continued to Discount', {
      from_step: currentStep,
      current_tier: user?.tier_type,
      target_tier: getNextTierSlug(nextTier),
    })
    setCurrentStep('discount')
  }

  const handleDiscountDecline = async () => {
    mixpanel.track('Downgrade Flow V2 Discount Declined', {
      current_tier: user?.tier_type,
      target_tier: getNextTierSlug(nextTier),
      saw_pause_offer_before_discount: sawPauseOfferBeforeDiscount,
      next_step: sawPauseOfferBeforeDiscount ? 'cancel_subscription' : 'pause_offer',
    })
    if (sawPauseOfferBeforeDiscount) {
      await handleCancelSubscription()
    } else {
      setTimingAfterDiscount(true)
      setCurrentStep('timing')
    }
  }

  const handleAcceptDiscount = async () => {
    try {
      setIsDiscountLoading(true)
      await applyDiscountOffer(user?.id)
      mixpanel.track('Downgrade Flow V2 Discount Accepted', {
        current_tier: user?.tier_type,
        target_tier: getNextTierSlug(nextTier),
      })
      addSuccessToast({ message: '60% discount applied to your next invoice.' })
      closedByActionRef.current = true
      handleClose()
    } catch (error) {
      mixpanel.track('Downgrade Flow V2 Discount Accept Failed', {
        current_tier: user?.tier_type,
        target_tier: getNextTierSlug(nextTier),
        error_message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'unknown',
      })
      addSuccessToast({
        message:
          error?.response?.data?.detail ||
          'Failed to apply discount. Please try again.',
        type: 'error',
      })
    } finally {
      setIsDiscountLoading(false)
    }
  }

  const handleCancelSubscription = async () => {
    try {
      setIsCancelLoading(true)
      await finishCancelDowngrade()
      mixpanel.track('Downgrade Flow V2 Completed', {
        previous_tier: user?.tier_type,
        step_reached: currentStep,
        target_tier: getNextTierSlug(nextTier),
      })
      setCurrentStep('feedback')
    } catch (error) {
      addSuccessToast({
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          'Failed to cancel your subscription. Please try again.',
        type: 'error',
      })
    } finally {
      setIsCancelLoading(false)
    }
  }

  const handleSubmitFeedback = async (answers) => {
    try {
      setIsFeedbackLoading(true)
      await submitFeedback({
        first_answer: answers.first,
        second_answer: answers.second,
        third_answer: answers.third,
        tier_downgrade_reason_id: downgradeInsights?.tier_downgrade_reason?.id,
      })
      mixpanel.track('Downgrade Flow V2 Feedback Submitted', {
        current_tier: user?.tier_type,
        target_tier: getNextTierSlug(nextTier),
        has_feedback: !!(answers.first || answers.second || answers.third),
      })
      closedByActionRef.current = true
      handleClose()
    } catch (error) {
      addSuccessToast({
        message:
          error?.response?.data?.detail ||
          'Failed to submit feedback. Please try again.',
        type: 'error',
      })
    } finally {
      setIsFeedbackLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      mixpanel.track('Downgrade Flow V2 Opened', {
        current_tier: user?.tier_type,
        target_tier: getNextTierSlug(nextTier),
      })

      getDowngradeInsights()
        .then((res) => {
          if (res?.status === 200) {
            setDowngradeInsights(res.data)
            const targetTierSlug = getNextTierSlug(nextTier)
            if (res.data?.tier_downgrade_reason?.id && targetTierSlug) {
              saveChangedTier({
                tier_downgrade_reason_id: res.data.tier_downgrade_reason.id,
                previous_tier: user?.tier_type,
                new_tier: targetTierSlug,
              }).catch(() => {})
            }
            onDataLoaded?.()
          }
        })
        .catch(() => {
          onDataLoaded?.()
        })
        .finally(() => setIsLoading(false))
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      mixpanel.track('Downgrade Flow V2 Step Viewed', {
        step: currentStep,
        current_tier: user?.tier_type,
        target_tier: getNextTierSlug(nextTier),
        ...(currentStep === 'timing'
          ? {
              timing_step_entry: timingAfterDiscount ? 'after_discount_decline' : 'reason_branch',
            }
          : {}),
      })
    }
  }, [currentStep, isOpen, timingAfterDiscount])

  const renderStep = () => {
    if (isLoading && STEPS_REQUIRING_INSIGHTS.has(currentStep)) {
      return (
        <div className="flex items-center justify-center py-v3-lg">
          <DLoading />
        </div>
      )
    }

    switch (currentStep) {
      case 'reason':
        return <StepReason onSelect={handleReasonSelect} />

      case 'expensive':
        return (
          <StepExpensive
            downgradeInsights={downgradeInsights}
            onPause={handlePause}
            onDowngrade={handleDowngrade}
            onLeave={handleGoToDiscount}
            isPauseLoading={isPauseLoading}
            isDowngradeLoading={isDowngradeLoading}
          />
        )

      case 'novalue':
        return (
          <StepNoValue
            downgradeInsights={downgradeInsights}
            onBookSession={handleBookSession}
            onLeave={handleGoToDiscount}
            isBookSessionLoading={isBookSessionLoading}
          />
        )

      case 'timing':
        return (
          <StepTiming
            downgradeInsights={downgradeInsights}
            onPause={handlePause}
            onLeave={timingAfterDiscount ? handleCancelSubscription : handleGoToDiscount}
            isPauseLoading={isPauseLoading}
            isCancelLoading={timingAfterDiscount ? isCancelLoading : false}
          />
        )

      case 'broken':
        return (
          <StepBroken
            onContactSupport={handleContactSupport}
            onLeave={handleGoToDiscount}
          />
        )

      case 'competitor':
        return (
          <StepCompetitor
            onAcceptDiscount={handleGoToDiscount}
            onDeclineOffer={handleCancelSubscription}
            isCancelLoading={isCancelLoading}
            mixpanelTargetTier={getNextTierSlug(nextTier)}
          />
        )

      case 'discount':
        return (
          <StepDiscount
            downgradeInsights={downgradeInsights}
            user={user}
            onAccept={handleAcceptDiscount}
            onDecline={handleDiscountDecline}
            isAcceptLoading={isDiscountLoading}
            isCancelLoading={isCancelLoading}
          />
        )

      case 'feedback':
        return (
          <StepFeedback
            onSubmit={handleSubmitFeedback}
            isLoading={isFeedbackLoading}
          />
        )

      default:
        return null
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={STEP_TITLES[currentStep] || ''}
      closeBackdrop={false}
      className="md:!w-[700px]"
    >
      {renderStep()}
    </Modal>
  )
}

DowngradeFlowModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  nextTier: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onDataLoaded: PropTypes.func,
}

DowngradeFlowModal.defaultProps = {
  nextTier: null,
  onDataLoaded: null,
}

export default DowngradeFlowModal
